import Hero from '../components/Hero';
import { services, companyInfo } from '../data';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

export default function Home() {
  return (
    <main className="relative bg-white min-h-screen">
      <SEO 
        title="FINANJU | Økonomisk og Juridisk Rådgivning for Virksomheder"
        description="FINANJU tilbyder professionel økonomisk og juridisk rådgivning. Vi forener finans og jura for at skabe struktur, overblik og vækst i din virksomhed."
        path="/"
      />

      <Hero
        title="Økonomisk og juridisk rådgivning med tyngde"
        subtitle="FINANJU forener finans og jura i en struktureret, kompetent og tillidsskabende rådgivning. Vi skaber overblik, så du kan fokusere på din forretning."
        ctaText="Kontakt FINANJU"
        ctaLink="/kontakt"
        image="/images/office-desk-with-plant.png"
      />

      {/* Intro / Content Section */}
      <section className="py-24 lg:py-40 bg-slate-50 relative overflow-hidden">
        {/* Decorative Background Element */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white hidden lg:block" />
        
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left Column: Visual */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-[#876a57]/10 rounded-sm translate-x-4 translate-y-4 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500" />
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm shadow-2xl">
                <img 
                  src="/images/desk-with-book-and-plant.png" 
                  alt="Nordisk kontorbord med plante og bog" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-white p-6 shadow-xl hidden md:block border-l-4 border-[#876a57]">
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-slate-900 leading-none">15+</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#876a57] mt-1">Års erfaring</span>
                </div>
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="lg:pl-12">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[1px] bg-[#876a57]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#876a57]">
                  Om FINANJU
                </span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold leading-[1.1] mb-8 text-slate-900">
                Vi forener <span className="text-[#876a57]">finans</span> og <span className="text-[#876a57]">jura</span> i ét stærkt fundament.
              </h2>
              
              <div className="space-y-6">
                <p className="text-slate-600 text-lg leading-relaxed">
                  Navnet FINANJU er skabt af vores to kernekompetencer. Vi har dedikeret os til at skabe gennemsigtighed og værdi gennem en struktureret tilgang til komplekse udfordringer.
                </p>
                
                <p className="text-slate-500 text-base leading-relaxed">
                  Uanset om der er tale om interim opgaver eller skræddersyede full-service løsninger, arbejder vi med professionel integritet og en praktisk tilgang, der sikrer fremdrift i din virksomhed.
                </p>
              </div>
              
              <div className="mt-12">
                <Link 
                  to="/om-finanju" 
                  className="group inline-flex items-center gap-4 px-8 py-4 bg-slate-900 text-white rounded-sm font-medium uppercase text-xs tracking-[0.15em] hover:bg-[#876a57] transition-all duration-300"
                >
                  Læs mere om os
                  <div className="w-6 h-[1px] bg-white group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Approach Section */}
      <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#876a57_1px,transparent_1px)] [background-size:40px_40px]" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Column: Text Content */}
            <div className="text-white">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[1px] bg-[#876a57]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#876a57]">
                  Kontakt FINANJU
                </span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold leading-[1.1] mb-8">
                Skal vi hjælpe med din <span className="text-[#876a57]">næste udfordring?</span>
              </h2>
              
              <p className="text-slate-400 text-lg leading-relaxed mb-10 max-w-xl">
                Vi tilbyder en uforpligtende dialog om, hvordan vi kan styrke din forretning gennem professionel finansiel og juridisk rådgivning.
              </p>
              
              <div className="space-y-6">
                {[
                  "Skræddersyede løsninger til din virksomhed",
                  "Høj faglighed og mange års erfaring",
                  "Direkte og ærlig kommunikation",
                  "Fokus på værdiskabelse og gennemsigtighed"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="mt-1 p-1 bg-[#876a57]/20 rounded-full">
                      <CheckCircle2 className="w-4 h-4 text-[#876a57]" />
                    </div>
                    <span className="text-slate-300 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-12 border-t border-slate-800">
                <div className="flex flex-col sm:flex-row gap-8">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Ring til os</p>
                    <a href={`tel:${companyInfo.phone}`} className="text-xl font-bold hover:text-[#876a57] transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Skriv til os</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-xl font-bold hover:text-[#876a57] transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Form */}
            <div className="relative">
              <div className="absolute -inset-4 bg-[#876a57]/5 rounded-sm blur-2xl" />
              <div className="relative">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}
